import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { LuMapPin, LuCheck, LuExternalLink } from 'react-icons/lu';
import { parse } from 'src/_shared/services/utils.js';
import { accentRgba } from './utils.js';

const FINAL_STATUSES = [
	'hired',
	'ineligible',
	'notHired',
	'declined',
	'inactive',
	'noResponse',
];

function getStepData(referral) {
	const { status, customStatus } = referral;

	let progress = 0;
	if (FINAL_STATUSES.includes(status)) {
		progress = 2;
	} else if (status === 'interviewing' || status === customStatus) {
		progress = 1;
	}

	let color = '#f59e0b';
	if (status === 'hired') color = '#22c55e';
	else if (FINAL_STATUSES.includes(status)) color = '#ef4444';

	const step2Label = customStatus || 'Interviewing';
	let step3Label = 'Hired';
	if (FINAL_STATUSES.includes(status) && status !== 'hired') {
		const map = {
			ineligible: 'Ineligible',
			notHired: 'Not Hired',
			declined: 'Declined',
			inactive: 'Inactive',
			noResponse: 'No Response',
		};
		step3Label = map[status] || status;
	}

	return {
		progress,
		color,
		labels: ['Started', step2Label, step3Label],
	};
}

function StatusSteps({ referral, accentColor }) {
	const { progress, color, labels } = getStepData(referral);

	return (
		<div
			style={{
				display: 'grid',
				gridTemplateColumns: 'auto 1fr auto 1fr auto',
				alignItems: 'start',
				padding: '0px 6px 12px',
			}}
		>
			{labels.map((label, i) => {
				const filled = i <= progress;
				const lineActive = i > 0 && i <= progress;
				return (
					<Fragment key={label}>
						{i > 0 && (
							<div
								className="flex items-center"
								style={{ paddingTop: 8, paddingLeft: 4, paddingRight: 4 }}
							>
								<div
									style={{
										width: '100%',
										height: 0,
										borderTop: lineActive
											? `2px solid ${color}`
											: '2px dashed #e2e8f0',
										transition: 'border-color 0.3s',
									}}
								/>
							</div>
						)}
						<div
							style={{
								position: 'relative',
								display: 'flex',
								justifyContent: 'center',
							}}
						>
							<div
								className="flex items-center justify-center"
								style={{
									width: 18,
									height: 18,
									borderRadius: '50%',
									background: filled ? color : '#fff',
									border: `2px solid ${filled ? color : '#e2e8f0'}`,
									transition: 'all 0.3s',
								}}
							>
								{filled && <LuCheck size={10} color="#fff" strokeWidth={3} />}
							</div>
							<span
								style={{
									position: 'absolute',
									top: 22,
									fontSize: 9,
									fontWeight: 500,
									color: filled ? '#1e293b' : '#475569',
									whiteSpace: 'nowrap',
									lineHeight: 1,
								}}
							>
								{label}
							</span>
						</div>
					</Fragment>
				);
			})}
		</div>
	);
}

function ApplicationCard({ referral, accentColor }) {
	const job = referral.job;
	if (!job) return null;

	const location =
		typeof job.location === 'string' ? parse(job.location) : job.location;
	const locationStr =
		typeof location === 'object' && location
			? location.isRemote
				? 'Remote'
				: [location.city, location.state].filter(Boolean).join(', ')
			: typeof location === 'string'
				? location
				: '';

	const date = referral.referralDate
		? new Date(referral.referralDate).toLocaleDateString()
		: '';

	const showApplyLink =
		job.internalJobLink && ['accepted', 'referred'].includes(referral.status);

	return (
		<div
			className="rounded-xl"
			style={{
				border: '1px solid #f1f5f9',
				padding: 14,
				background: '#fff',
				transition: 'border-color 0.2s, box-shadow 0.2s',
			}}
			onMouseOver={(e) => {
				e.currentTarget.style.borderColor = '#e2e8f0';
				e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.04)';
			}}
			onMouseOut={(e) => {
				e.currentTarget.style.borderColor = '#f1f5f9';
				e.currentTarget.style.boxShadow = 'none';
			}}
		>
			<div className="flex items-start justify-between gap-2">
				<h3
					title={job.title}
					style={{
						fontSize: 14,
						fontWeight: 600,
						margin: 0,
						lineHeight: 1.3,
						display: '-webkit-box',
						WebkitLineClamp: 2,
						WebkitBoxOrient: 'vertical',
						overflow: 'hidden',
					}}
				>
					<Link
						to={`/browsejobs/${job.id}`}
						style={{
							color: '#1e293b',
							textDecoration: 'none',
						}}
						onMouseOver={(e) => (e.currentTarget.style.color = accentColor)}
						onMouseOut={(e) => (e.currentTarget.style.color = '#1e293b')}
					>
						{job.title}
					</Link>
				</h3>
				{date && (
					<span style={{ fontSize: 11, color: '#475569', flexShrink: 0 }}>
						{date}
					</span>
				)}
			</div>

			{(locationStr || showApplyLink) && (
				<div
					className="mt-1 flex items-center justify-between gap-2"
					style={{ fontSize: 12 }}
				>
					{locationStr ? (
						<span
							className="flex items-center gap-1"
							style={{ color: '#475569' }}
						>
							<LuMapPin size={11} />
							{locationStr}
						</span>
					) : (
						<span />
					)}
					{showApplyLink && (
						<a
							href={job.internalJobLink}
							target="_blank"
							rel="noreferrer"
							className="flex items-center gap-1"
							style={{
								fontWeight: 600,
								color: accentColor,
								textDecoration: 'none',
								flexShrink: 0,
							}}
						>
							Apply Link
							<LuExternalLink size={11} />
						</a>
					)}
				</div>
			)}

			<div style={{ marginTop: 12 }}>
				<StatusSteps referral={referral} accentColor={accentColor} />
			</div>
		</div>
	);
}

export default ApplicationCard;
